"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const localforage_1 = __importDefault(require("localforage"));
const Utils_1 = require("./Utils");
/**
 * Framework for caching large files downloaded from a remote stream.
 */
class LocalCache {
    constructor(settings) {
        const totalPercentage = settings.poolTypes.reduce((acc, pool) => acc + pool.allocationRatio, 0);
        if (totalPercentage > 1.0 ||
            settings.poolTypes.find((config) => config.allocationRatio < 0)) {
            throw new Error('Invalid cache allocation ratios.');
        }
        this.settings = settings;
        this.localForage = localforage_1.default.createInstance({
            driver: localforage_1.default.INDEXEDDB,
            name: 'SkandDB',
            storeName: settings.name,
            size: settings.capacity,
        });
    }
    /**
     * Register a file in the cache, evicting the not recently used entries until it fits.
     *
     * @param cacheKey
     * @param url
     * @param blob
     */
    register(cacheKey, url, blob) {
        return __awaiter(this, void 0, void 0, function* () {
            const pool = yield (0, Utils_1.asyncFind)(this.settings.poolTypes, (pool) => __awaiter(this, void 0, void 0, function* () { return yield pool.isMember(url, blob); }));
            if (pool === undefined) {
                return;
            }
            // Check if the new entry fits in the pool
            const poolSize = pool.allocationRatio * this.settings.capacity;
            if (blob.size > poolSize) {
                return;
            }
            // Get list of existing entries in the pool
            const entries = [];
            yield this.localForage.iterate((value, key) => {
                if (value.poolName === pool.name) {
                    entries.push([key, value]);
                }
            });
            // Sort the entries by the recently-used timestamp
            entries.sort((a, b) => {
                return a[1].lastUsed.getTime() - b[1].lastUsed.getTime();
            });
            // Compute the current total size
            let totalSize = entries.reduce((acc, [_, entry]) => acc + entry.content.size, 0);
            // Evict the not-recently-used entries until the new entry fits
            while (entries.length > 0 &&
                totalSize + blob.size > this.settings.capacity * pool.allocationRatio) {
                const notRecentlyUsed = entries.shift();
                if (notRecentlyUsed) {
                    const [key, entry] = notRecentlyUsed;
                    yield this.localForage.removeItem(key);
                    totalSize -= entry.content.size;
                }
            }
            yield this.localForage.setItem(cacheKey, {
                url,
                content: blob,
                poolName: pool.name,
                lastUsed: new Date(),
            });
        });
    }
    /**
     * Fetch a file from the cache or from the remote stream.
     *
     * @param url
     * @param options
     * @returns
     */
    fetch(url, options) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const cacheKey = (_a = options === null || options === void 0 ? void 0 : options.cacheKey) !== null && _a !== void 0 ? _a : url;
            const entry = yield this.localForage.getItem(cacheKey);
            if (entry !== null) {
                // Update the entry's recently-used timestamp
                const { content, poolName } = entry;
                yield this.localForage.setItem(cacheKey, {
                    url,
                    content,
                    poolName,
                    lastUsed: new Date(),
                });
                return content;
            }
            else {
                // Fetch the file from the remote stream and register it in the cache
                const init = {
                    method: 'GET',
                };
                if (options !== undefined && options.headers !== undefined) {
                    init.headers = options.headers;
                }
                const response = yield fetch(url, init);
                // Some browsers return HTTP Status 0 when using non-http protocol
                // e.g. 'file://' or 'data://'. Handle as success.
                if (response.status === 200 || response.status === 0) {
                    const blob = yield response.blob();
                    yield this.register(cacheKey, url, blob);
                    return blob;
                }
                else {
                    throw new Error(`GET request to ${url} failed with status ${response.status}`);
                }
            }
        });
    }
    /**
     * Evict a file from the cache, if it exists.
     *
     * @param key
     * @returns
     */
    evict(key) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.localForage.removeItem(key);
        });
    }
    /**
     * Clear the cache.
     *
     * @returns
     */
    clear() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.localForage.clear();
        });
    }
    /**
     * Get a map of cached files to their urls.
     *
     * @returns
     */
    getEntries() {
        return __awaiter(this, void 0, void 0, function* () {
            const map = new Map();
            const keys = yield this.localForage.keys();
            for (const key of keys) {
                const entry = yield this.localForage.getItem(key);
                if (entry !== null) {
                    map.set(key, entry.content);
                }
            }
            return map;
        });
    }
    /**
     * Get the cache settings.
     *
     * @returns
     */
    getSettings() {
        return this.settings;
    }
}
exports.default = LocalCache;
